/** Figures of one acquisition, from the rows of its `photos.gpkg` (spec 2026-10-09 §2.2). */
import { DEFAULT_MAX_TILT_DEG } from '@/map/layers';
import type { PhotoProperties } from '@/map/photoSource';

export interface Summary { median: number; min: number; max: number; p95: number }
/** `excluded`: of `count`, the photos the photogrammetry leaves out (tilt histogram with a known limit only). */
export interface HistogramBin { from: number; to: number; count: number; excluded?: number }
export type PhotoMetric = 'agl_m' | 'forward_overlap_pct' | 'side_overlap_pct' | 'tilt_deg' | 'geo_error_h_m';
export interface FlightSeries { flight: string; points: { minutes: number; agl: number }[] }
export interface PhotoStats {
  overview: { count: number; flights: string[]; start: string | null; end: string | null; flownMinutes: number | null; totalBytes: number | null; cameras: string[]; drones: string[] };
  quality: { agl: Summary | null; speed: Summary | null; forward: Summary | null; side: Summary | null; tilt: Summary | null };
  georeferencing: { gpsStatus: { status: string; count: number; pct: number }[]; errorH: Summary | null; errorV: Summary | null };
  histograms: Partial<Record<PhotoMetric, HistogramBin[]>>;
  altitude: FlightSeries[];
}

/** Fraction digits that keep neighbouring bin edges distinct; one when the bin has no width. */
export function binDigits(bin: HistogramBin): number {
  const width = bin.to - bin.from;
  if (!(width > 0)) return 1;
  return Math.min(3, Math.max(0, 1 - Math.floor(Math.log10(width))));
}

/** At most `max` items, evenly strided, always keeping the first and the last. */
export function thin<T>(items: T[], max = ALTITUDE_MAX_POINTS): T[] {
  if (items.length <= max) return items;
  return Array.from({ length: max }, (_, i) => items[Math.round((i * (items.length - 1)) / (max - 1))]!);
}

/**
 * The altitude series as one table for a line chart: a row per photo, keyed by its minute, with the
 * height under the series' key (`f0`, `f1`, …) and nothing under the others. A key that is not the
 * flight name keeps a dot or bracket in the name from being read as a path into the row.
 */
export function altitudeRows(series: FlightSeries[]): { minutes: number; [key: string]: number }[] {
  const rows = series.flatMap((s, i) => s.points.map((pt) => ({ minutes: pt.minutes, [altitudeKey(i)]: pt.agl })));
  return rows.sort((a, b) => a.minutes - b.minutes);
}

export const altitudeKey = (i: number) => `f${i}`;

export const HISTOGRAM_METRICS: PhotoMetric[] = ['agl_m', 'forward_overlap_pct', 'side_overlap_pct', 'tilt_deg', 'geo_error_h_m'];
const DEFAULT_BINS = 20;
// Per flight; a flight is a few minutes across the chart width, so more points draw the same line and only slow the chart.
export const ALTITUDE_MAX_POINTS = 300;
const NO_FLIGHT = '—';

const numbers = (values: (number | null)[]) => values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));

function quantile(sorted: number[], q: number): number {
  const at = (sorted.length - 1) * q;
  const lo = Math.floor(at);
  const hi = Math.ceil(at);
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (at - lo);
}

export function summarize(values: (number | null)[]): Summary | null {
  const vs = numbers(values).sort((a, b) => a - b);
  if (!vs.length) return null;
  return { median: quantile(vs, 0.5), min: vs[0]!, max: vs[vs.length - 1]!, p95: quantile(vs, 0.95) };
}

export function histogram(values: (number | null)[], bins = DEFAULT_BINS): HistogramBin[] {
  const vs = numbers(values);
  if (!vs.length) return [];
  const min = Math.min(...vs);
  const max = Math.max(...vs);
  if (min === max) return [{ from: min, to: max, count: vs.length }];
  const width = (max - min) / bins;
  const out = Array.from({ length: bins }, (_, i) => ({ from: min + i * width, to: i === bins - 1 ? max : min + (i + 1) * width, count: 0 }));
  for (const v of vs) out[Math.min(bins - 1, Math.floor((v - min) / width))]!.count += 1;
  return out;
}

/**
 * The tilt histogram split at the limit. Tilts the camera reports in a few steps (a fixed gimbal:
 * 10.0° and 10.1°) get a bar per value, so the two sides of the limit are two bars; otherwise the
 * limit is a bin edge and bins are closed on the right, so a bin holds only kept (tilt ≤ limit)
 * or only left-out photos. Each bin counts the two apart anyway: an edge off by a rounding error
 * cannot misplace a photo.
 */
export function tiltHistogram(values: (number | null)[], limit: number, bins = DEFAULT_BINS): HistogramBin[] {
  const vs = numbers(values).sort((a, b) => a - b);
  if (!vs.length) return [];
  const split = (from: number, to: number, list: number[]) => ({ from, to, count: list.length, excluded: list.filter((v) => v > limit).length });
  const steps = [...new Set(vs)];
  if (steps.length <= bins) return steps.map((v) => split(v, v, vs.filter((x) => x === v)));
  const min = vs[0]!;
  const max = vs[vs.length - 1]!;
  const width = (max - min) / bins;
  const start = limit > min && limit < max ? limit - Math.ceil((limit - min) / width) * width : min;
  const n = Math.max(1, Math.ceil((max - start) / width - 1e-9));
  const buckets: number[][] = Array.from({ length: n }, () => []);
  for (const v of vs) buckets[Math.min(n - 1, Math.max(0, Math.ceil((v - start) / width - 1e-9) - 1))]!.push(v);
  return buckets.map((list, i) => split(start + i * width, i === n - 1 ? max : start + (i + 1) * width, list));
}

/** The photos the photogrammetry leaves out for their tilt (spec: the ODM split drops tilt > limit). */
export interface TiltExclusion {
  /** The limits applied, one per distinct value over the acquisitions. */
  limits: number[];
  /** Some acquisition did not say its limit, so the default was assumed. */
  assumed: boolean;
  total: number;
  excluded: number;
  /** The tilts of the left-out photos. */
  tilts: Summary | null;
}

/**
 * Counted as the photogrammetry filters: tilt strictly above the acquisition's limit. A photo
 * without a tilt is kept (nothing to filter it by) but counted in the total. `null` when no photo
 * has a tilt at all.
 */
export function tiltExclusion(groups: { photos: PhotoProperties[]; limit?: number | null }[]): TiltExclusion | null {
  let total = 0;
  let withTilt = 0;
  const left: number[] = [];
  const limits = new Set<number>();
  let assumed = false;
  for (const g of groups) {
    if (!g.photos.length) continue;
    const limit = g.limit ?? DEFAULT_MAX_TILT_DEG;
    assumed ||= g.limit === null || g.limit === undefined;
    limits.add(limit);
    total += g.photos.length;
    for (const v of numbers(g.photos.map((ph) => ph.tilt_deg))) {
      withTilt += 1;
      if (v > limit) left.push(v);
    }
  }
  if (!withTilt) return null;
  return { limits: [...limits].sort((a, b) => a - b), assumed, total, excluded: left.length, tilts: summarize(left) };
}

/** Fraction digits (1–3) that keep a left-out tilt from reading as the limit (10.04 above 10 is 10,04, not 10,0). */
export function tiltDigits(tilt: number, limit: number): number {
  for (let d = 1; d < 3; d++) if (Number(tilt.toFixed(d)) > limit) return d;
  return 3;
}

const distinct = (values: (string | null)[]) => [...new Set(values.filter((v): v is string => !!v))].sort();
const ms = (iso: string) => new Date(iso).getTime();

function byFlight(photos: PhotoProperties[]): Map<string, PhotoProperties[]> {
  const out = new Map<string, PhotoProperties[]>();
  for (const ph of photos) {
    const key = ph.flight ?? NO_FLIGHT;
    let bucket = out.get(key);
    if (!bucket) {
      bucket = [];
      out.set(key, bucket);
    }
    bucket.push(ph);
  }
  return out;
}

/** `tiltLimit`: the one limit of the photos, splitting the tilt histogram into kept and left out. */
export function photoStats(photos: PhotoProperties[], { tiltLimit }: { tiltLimit?: number } = {}): PhotoStats {
  const times = photos.map((ph) => ph.time).filter((t): t is string => !!t && Number.isFinite(ms(t))).sort();
  const flights = byFlight(photos);
  let flown = 0;
  let anyFlown = false;
  const altitude: FlightSeries[] = [];
  for (const [flight, list] of [...flights].sort(([a], [b]) => a.localeCompare(b))) {
    const timed = list.filter((ph) => {
      if (!ph.time) return false;
      const timestamp = ms(ph.time);
      return Number.isFinite(timestamp);
    }).sort((a, b) => a.time!.localeCompare(b.time!));
    if (timed.length) {
      const start = ms(timed[0]!.time!);
      flown += (ms(timed[timed.length - 1]!.time!) - start) / 60000;
      anyFlown = true;
      const points = timed.filter((ph) => typeof ph.agl_m === 'number').map((ph) => ({ minutes: (ms(ph.time!) - start) / 60000, agl: ph.agl_m! }));
      if (points.length) altitude.push({ flight, points: thin(points) });
    }
  }
  const sizes = numbers(photos.map((ph) => ph.size_bytes));
  const statusCounts = new Map<string, number>();
  for (const ph of photos) if (ph.gps_status) statusCounts.set(ph.gps_status, (statusCounts.get(ph.gps_status) ?? 0) + 1);
  const withStatus = [...statusCounts.values()].reduce((a, b) => a + b, 0);
  const histograms: Partial<Record<PhotoMetric, HistogramBin[]>> = {};
  for (const m of HISTOGRAM_METRICS) {
    const values = photos.map((ph) => ph[m]);
    const bins = m === 'tilt_deg' && tiltLimit !== undefined ? tiltHistogram(values, tiltLimit) : histogram(values);
    if (bins.length) histograms[m] = bins;
  }
  return {
    overview: {
      count: photos.length,
      flights: [...flights.keys()].filter((f) => f !== NO_FLIGHT).sort(),
      start: times[0] ?? null,
      end: times[times.length - 1] ?? null,
      flownMinutes: anyFlown ? flown : null,
      totalBytes: sizes.length ? sizes.reduce((a, b) => a + b, 0) : null,
      cameras: distinct(photos.map((ph) => ph.model)),
      drones: distinct(photos.map((ph) => ph.drone_model)),
    },
    quality: {
      agl: summarize(photos.map((ph) => ph.agl_m)),
      speed: summarize(photos.map((ph) => ph.speed)),
      forward: summarize(photos.map((ph) => ph.forward_overlap_pct)),
      side: summarize(photos.map((ph) => ph.side_overlap_pct)),
      tilt: summarize(photos.map((ph) => ph.tilt_deg)),
    },
    georeferencing: {
      gpsStatus: [...statusCounts].map(([status, count]) => ({ status, count, pct: (count / withStatus) * 100 })).sort((a, b) => b.count - a.count),
      errorH: summarize(photos.map((ph) => ph.geo_error_h_m)),
      errorV: summarize(photos.map((ph) => ph.geo_error_v_m)),
    },
    histograms,
    altitude,
  };
}
